import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/LanguageContext";

export const WelcomePopup = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("evara_popup_shown")) return;
    const tid = setTimeout(() => setOpen(true), 2000);
    return () => clearTimeout(tid);
  }, []);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem("evara_popup_shown", "1");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success(t("common.successNewsletter"));
      setEmail("");
      close();
    } catch {
      toast.error(t("common.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="welcome-popup-overlay"
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            data-testid="welcome-popup"
            className="relative w-full max-w-xl bg-[#6B5CE7] rounded-[28px] px-8 md:px-14 py-12 md:py-16 text-white shadow-2xl"
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
          >
            <button
              onClick={close}
              data-testid="welcome-popup-close"
              className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
              aria-label={t("popup.close")}
            >
              <X size={20} />
            </button>

            <h2 className="font-serif text-4xl md:text-5xl font-bold uppercase tracking-wide mb-3">
              {t("popup.title")}
            </h2>
            <p className="text-white/85 text-base md:text-lg font-light mb-8 max-w-md">
              {t("popup.subtitle")}
            </p>

            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("popup.placeholder")}
                data-testid="welcome-popup-email"
                className="flex-1 px-5 py-3.5 rounded-full bg-white text-[#1A1528] placeholder:text-[#1A1528]/40 outline-none focus:ring-2 focus:ring-[#F5E6A3]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="welcome-popup-submit"
                className="px-7 py-3.5 rounded-full bg-white text-[#6B5CE7] font-medium hover:bg-[#F5E6A3] transition-colors disabled:opacity-60"
              >
                {loading ? "..." : t("common.envoyer")}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
